import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api';

const API_BASE_URL =
    typeof apiConfig === 'string'
        ? apiConfig
        : NAMED_API_BASE_URL || 'https://pms.burrayhr.com';

/* ------------------------------------------------------------------ */
/*  Shimmer skeleton                                                   */
/* ------------------------------------------------------------------ */
const Shimmer = ({ width = '100%', height = '1rem', className = '', rounded = '4px' }) => (
    <span
        className={`placeholder placeholder-wave col-12 ${className}`}
        style={{ width, height, minHeight: height, borderRadius: rounded, display: 'block' }}
    />
);

const CardSkeleton = ({ delay = 0 }) => (
    <div className="col-md-6 col-xl-4 d-flex" style={{ animationDelay: `${delay}ms` }}>
        <div className="card flex-fill">
            <div className="card-body placeholder-glow">
                <div className="d-flex align-items-center gap-3 mb-3">
                    <Shimmer width="48px" height="48px" rounded="12px" />
                    <div className="flex-grow-1">
                        <Shimmer width="60%" height="14px" className="mb-2" />
                        <Shimmer width="40%" height="12px" />
                    </div>
                </div>
                <Shimmer width="80%" height="12px" className="mb-2" />
                <Shimmer width="55%" height="12px" />
            </div>
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  Staggered reveal                                                   */
/* ------------------------------------------------------------------ */
const AnimatedCard = ({ children, index = 0, className = '' }) => (
    <div className={`stats-card-reveal ${className}`} style={{ animationDelay: `${index * 70}ms` }}>
        {children}
    </div>
);

/* ------------------------------------------------------------------ */
/*  Avatar colour rotation                                             */
/* ------------------------------------------------------------------ */
const avatarColors = ['bg-teal', 'bg-warning', 'bg-orange', 'bg-success', 'bg-info', 'bg-indigo', 'bg-pink'];

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
const CostCenters = () => {
    const [costCenters, setCostCenters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    /* ── Edit modal state ── */
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editCostCenter, setEditCostCenter] = useState(null);
    const [editName, setEditName] = useState('');
    const [editSaving, setEditSaving] = useState(false);

    const animStyles = `
        .stats-card-reveal {
            animation: statsFadeInUp 0.45s ease both;
        }
        @keyframes statsFadeInUp {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        .placeholder-glow .placeholder {
            animation: placeholder-glow 1.4s ease-in-out infinite;
        }
        .cc-modal-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.45);
            z-index: 1050;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .cc-modal {
            background: #fff;
            border-radius: 12px;
            padding: 32px;
            width: 100%;
            max-width: 440px;
            box-shadow: 0 16px 48px rgba(0,0,0,0.18);
            position: relative;
            animation: statsFadeInUp 0.3s ease both;
        }
        .cc-card-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            font-size: 20px;
            color: #fff;
        }
        .cc-hover-card {
            transition: box-shadow 0.2s ease, transform 0.2s ease;
        }
        .cc-hover-card:hover {
            box-shadow: 0 8px 24px rgba(0,0,0,0.10) !important;
            transform: translateY(-2px);
        }
    `;

    /* ── Fetch ── */
    const fetchCostCenters = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = Cookies.get('token');
            const response = await axios.get(`${API_BASE_URL}/api/course-centers/`, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            setCostCenters(Array.isArray(response.data) ? response.data : []);
        } catch {
            setError('Failed to fetch cost centers.');
            toast.error('Failed to fetch cost centers.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCostCenters();
    }, [fetchCostCenters]);

    /* ── Edit handlers ── */
    const handleEdit = (center) => {
        setEditCostCenter(center);
        setEditName(center.name);
        setEditModalOpen(true);
    };

    const handleEditClose = () => {
        setEditModalOpen(false);
        setEditCostCenter(null);
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        setEditSaving(true);
        try {
            const token = Cookies.get('token');
            await axios.put(
                `${API_BASE_URL}/api/course-centers/${editCostCenter.id}/`,
                { name: editName },
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            toast.success('Cost center updated successfully!');
            handleEditClose();
            fetchCostCenters();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update cost center.');
        } finally {
            setEditSaving(false);
        }
    };

    /* ── Initials from name ── */
    const getInitials = (name = '') =>
        name
            .split(/\s+/)
            .slice(0, 2)
            .map((w) => w[0]?.toUpperCase())
            .join('');

    return (
        <>
            <style>{animStyles}</style>
            <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} />

            <div
                data-sidebar-skin="dark"
                data-header-skin="light"
                data-navbar-brand-skin="dark"
                data-sidebar-state="expand"
            >
                <Topbar />
                <Sidebar />

                <div className="page-wrapper">
                    <div className="content pb-0">

                        {/* ─── Page Header ─── */}
                        <AnimatedCard index={0}>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                                <div>
                                    <h4 className="fw-bold mb-1">Cost Centers</h4>
                                    <p className="text-muted mb-0 fs-13">
                                        View and manage all your organisation's cost centers
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-sm btn-outline-white border"
                                        onClick={fetchCostCenters}
                                        disabled={loading}
                                        title="Refresh"
                                    >
                                        <i
                                            className="ti ti-refresh me-1"
                                            style={{
                                                display: 'inline-block',
                                                transition: 'transform 0.4s',
                                                transform: loading ? 'rotate(360deg)' : 'none',
                                            }}
                                        />
                                        Refresh
                                    </button>
                                    <Link to="/hr/cost-centers/add" className="btn btn-sm btn-primary">
                                        <i className="ti ti-plus me-1" />
                                        Add Cost Center
                                    </Link>
                                </div>
                            </div>
                        </AnimatedCard>

                        {/* ─── Content ─── */}
                        {loading ? (
                            <div className="row">
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                    <CardSkeleton key={i} delay={i * 80} />
                                ))}
                            </div>
                        ) : error ? (
                            <AnimatedCard index={1}>
                                <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
                                    <i className="ti ti-alert-circle fs-20" />
                                    <div>
                                        {error}{' '}
                                        <button
                                            className="btn btn-sm btn-outline-danger ms-2"
                                            onClick={fetchCostCenters}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                </div>
                            </AnimatedCard>
                        ) : costCenters.length === 0 ? (
                            <AnimatedCard index={1}>
                                <div className="card">
                                    <div className="card-body text-center py-5">
                                        <i className="ti ti-building-off fs-48 text-muted d-block mb-3" />
                                        <h5 className="fw-semibold text-muted mb-2">No Cost Centers Found</h5>
                                        <p className="text-muted fs-13 mb-3">
                                            Get started by adding your first cost center.
                                        </p>
                                        <Link to="/hr/cost-centers/add" className="btn btn-primary btn-sm">
                                            <i className="ti ti-plus me-1" />
                                            Add Cost Center
                                        </Link>
                                    </div>
                                </div>
                            </AnimatedCard>
                        ) : (
                            <div className="row">
                                {costCenters.map((center, idx) => {
                                    const colorClass = avatarColors[idx % avatarColors.length];
                                    const initials = getInitials(center.name);
                                    const employeeCount =
                                        center.employee_count !== undefined
                                            ? center.employee_count
                                            : center.employees?.length ?? 'N/A';
                                    const createdAt = center.created_at
                                        ? new Date(center.created_at).toLocaleDateString(undefined, {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                        })
                                        : '—';

                                    return (
                                        <div key={center.id || idx} className="col-md-6 col-xl-4 d-flex mt-2">
                                            <AnimatedCard index={idx + 1} className="flex-fill">
                                                <div className="card flex-fill cc-hover-card shadow-sm h-100">
                                                    <div className="card-body">
                                                        {/* Top row: icon + name + edit */}
                                                        <div className="d-flex align-items-start gap-3 mb-3">
                                                            <div className={`cc-card-icon ${colorClass}`}>
                                                                <span className="fs-14 fw-bold">{initials}</span>
                                                            </div>
                                                            <div className="flex-grow-1 min-w-0">
                                                                <h6 className="fw-bold mb-0 text-truncate">
                                                                    {center.name}
                                                                </h6>
                                                                <p className="text-muted fs-12 mb-0">Cost Center</p>
                                                            </div>
                                                            <button
                                                                className="btn btn-sm btn-icon btn-outline-white border"
                                                                onClick={() => handleEdit(center)}
                                                                title="Edit"
                                                            >
                                                                <i className="ti ti-edit" />
                                                            </button>
                                                        </div>

                                                        {/* Stats row */}
                                                        <div className="d-flex gap-3">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="avatar avatar-sm bg-light-teal text-teal d-flex align-items-center justify-content-center rounded-circle">
                                                                    <i className="ti ti-users fs-14" />
                                                                </div>
                                                                <div>
                                                                    <p className="fs-11 text-muted mb-0">Employees</p>
                                                                    <p className="fs-13 fw-semibold mb-0">
                                                                        {employeeCount}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="vr" />
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="avatar avatar-sm bg-light-primary text-primary d-flex align-items-center justify-content-center rounded-circle">
                                                                    <i className="ti ti-calendar fs-14" />
                                                                </div>
                                                                <div>
                                                                    <p className="fs-11 text-muted mb-0">Created</p>
                                                                    <p className="fs-12 fw-semibold mb-0">{createdAt}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </AnimatedCard>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                        <p className="text-dark mb-0">
                            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
                        </p>
                    </div>
                </div>
            </div>

            {/* ─── Edit Modal ─── */}
            {editModalOpen && (
                <div className="cc-modal-backdrop" onClick={handleEditClose}>
                    <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
                        {/* Close */}
                        <button
                            className="btn btn-sm btn-icon btn-outline-white border position-absolute"
                            style={{ top: 16, right: 16 }}
                            onClick={handleEditClose}
                            disabled={editSaving}
                            title="Close"
                        >
                            <i className="ti ti-x" />
                        </button>

                        {/* Header */}
                        <div className="d-flex align-items-center gap-3 mb-4">
                            <div className="avatar avtar-lg bg-primary">
                                <i className="ti ti-building fs-22 text-white" />
                            </div>
                            <div>
                                <h5 className="fw-bold mb-0">Edit Cost Center</h5>
                                <p className="text-muted fs-13 mb-0">Update the cost center name</p>
                            </div>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleEditSave}>
                            <div className="mb-4">
                                <label htmlFor="editCostCenterName" className="form-label fw-semibold">
                                    Cost Center Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    id="editCostCenterName"
                                    type="text"
                                    className="form-control"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    required
                                    disabled={editSaving}
                                    placeholder="e.g. Finance & Operations"
                                    autoFocus
                                />
                            </div>

                            <div className="d-flex gap-2 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={handleEditClose}
                                    disabled={editSaving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={editSaving || !editName.trim()}
                                >
                                    {editSaving ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ti ti-device-floppy me-1" />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default CostCenters;